'use client';

import HeadlineField from '@/components/customFields/HeadlineField';
import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import { HeadlineTag } from '@/components/customFields/types';
import { RichText } from '@/components/richText/RichText';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/tabs/Tabs';
import { hasContent } from '@/helper/RichText';
import { shouldShowCalculatorStoryFooter } from '@/helper/calculatorFooter';
import useCalculatorTabs from '@/hooks/useCalculatorTabs';
import { useSettings } from '@/providers/SettingsProvider';
import classes from '@/storyblok/calculator/Calculators.module.scss';
import { a11yProps } from '@/storyblok/tabs/Tabs';
import type { PartialCalculatorCardStoryblok } from '@/types/component-types-sb';
import { StoryblokServerComponent } from '@storyblok/react/rsc';
import classNames from 'classnames';

const CalculatorCard: React.FC<{
  blok: PartialCalculatorCardStoryblok;
}> = ({ blok }) => {
  const settings = useSettings();
  const title = settings?.calculator_title || '';
  const footer = settings?.calculator_footer;
  const { tabs, tab, setTab } = useCalculatorTabs(blok.tabs);

  if (tabs.length === 0) {
    return null;
  }

  const activeTab = tabs.find((t) => t.uuid === tab) ?? tabs[0];
  const showActiveTabFooter = shouldShowCalculatorStoryFooter(activeTab?.text, {
    hideFooterText: activeTab?.hideFooterText,
  });
  let footerRichText: PartialCalculatorCardStoryblok['content'];
  if (hasContent(blok.content)) {
    footerRichText = blok.content;
  } else if (showActiveTabFooter) {
    footerRichText = activeTab?.text;
  } else if (!activeTab?.hideFooterText && hasContent(footer)) {
    footerRichText = footer;
  }
  const hasFooter = hasContent(footerRichText);
  const renderingTitle = blok.title || activeTab?.title || title;

  return (
    <div>
      <div
        className={classNames(classes.calculatorCard, { [classes.hasTabs]: tabs.length > 1 })}
        data-testid="calculator-card"
        data-calculator-count={blok.tabs.length}
      >
        {tabs.length > 1 ? (
          <>
            <Tabs
              className={classes.calculatorTabs}
              value={tab}
              onValueChange={(value) => {
                if (value != null) {
                  setTab(String(value));
                }
              }}
            >
              <TabsList scrollable variant="card">
                {tabs.map((tabItem) => (
                  <TabsTrigger key={`tab-${tabItem.uuid}`} value={tabItem.uuid} {...a11yProps(tabItem.uuid)}>
                    <div className={classes.tabIcon}>
                      <StoryblokImage
                        image={tabItem.icon}
                        ariaHidden={true}
                        width={{ pxMobile: 24, pxTablet: 24, pxDesktop: 24 }}
                      />
                    </div>
                    <span>{tabItem.tabTitle}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              <HeadlineField className={classes.calculatorMainTitle} tag={HeadlineTag.H2} style={HeadlineTag.H4}>
                {renderingTitle}
              </HeadlineField>

              {tabs.map((tabItem) => (
                <TabsContent
                  key={`tab-panel-${tabItem.uuid}`}
                  aria-labelledby={`tab-${tabItem.uuid}`}
                  className={classes.calculatorPanel}
                  data-testid={`tab-panel-${tabItem.uuid}`}
                  id={`tab-panel-${tabItem.uuid}`}
                  keepMounted
                  value={tabItem.uuid}
                >
                  <StoryblokServerComponent
                    blok={tabItem.content}
                    key={`tab-${tabItem.uuid}`}
                    hideTitle={true}
                    hideFooter={true}
                    calculatorStoryId={String(tabItem.id)}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </>
        ) : (
          <StoryblokServerComponent
            blok={tabs[0]?.content}
            hideTitle={false}
            tag={HeadlineTag.H3}
            hideFooter={true}
            calculatorStoryId={tabs[0] ? String(tabs[0].id) : undefined}
          />
        )}
      </div>

      {hasFooter && (
        <div className={classes.calculatorFooter}>
          <RichText text={footerRichText} />
        </div>
      )}
    </div>
  );
};

export default CalculatorCard;
